/** Số đếm ở menu trái và thẻ người dùng, đọc từ các API đã có (cùng query key với các màn). */
import { useQuery } from "@tanstack/react-query";
import { z } from "zod";
import { Achievements, NoteList, RevisionStats, Score, TestSummary } from "@ca/shared";
import { api } from "./api.ts";
import { useRead } from "./revision-ui.tsx";
import { useDailyRounds, useFocus } from "./focus.tsx";
import { useToday } from "./productivity.tsx";
import { NOTE_METHODS, type Count } from "./navigation.ts";

export function useTests(uid: string, kind: string) {
  return useQuery({
    queryKey: ["tests", uid, kind],
    queryFn: async () => z.array(TestSummary).parse(await api(`/v1/tests?kind=${kind}`)),
  });
}
export function useScores(uid: string) {
  return useQuery({
    queryKey: ["scores", uid],
    queryFn: async () => z.array(Score).parse(await api("/v1/me/scores")),
  });
}
/** Điểm full test gần nhất (danh sách server trả mới nhất trước). */
export function latestScore(scores: z.infer<typeof Score>[] | undefined) {
  return scores?.find((s) => s.result.totalScore !== null)?.result.totalScore ?? null;
}
export function useAchievements(uid: string) {
  return useQuery({
    queryKey: ["achievements", uid],
    queryFn: async () => Achievements.parse(await api("/v1/me/achievements", { accountId: uid })),
  });
}
/** Tổng số ghi chú (và giới hạn bản miễn phí) theo cách ghi; `method` rỗng là mọi ghi chú. */
export function useNoteTotal(uid: string, method = "") {
  const params = new URLSearchParams({ q: "", offset: "0", limit: "1" });
  if (method) params.set("method", method);
  return useQuery({
    queryKey: ["notes", uid, "count", method],
    queryFn: async () =>
      NoteList.parse(await api(`/v1/me/notes?${params.toString()}`, { accountId: uid })),
  });
}

export function useSideCounts(uid: string): Partial<Record<Count, string | undefined>> {
  const ets = useTests(uid, "ets"),
    mock = useTests(uid, "mock"),
    stats = useRead(uid, "/v1/me/stats/summary", RevisionStats),
    scores = useScores(uid),
    focus = useFocus(uid),
    dailyRounds = useDailyRounds(),
    today = useToday(uid),
    notes = useNoteTotal(uid),
    cornell = useNoteTotal(uid, "cornell"),
    box = useNoteTotal(uid, "box"),
    map = useNoteTotal(uid, "map"),
    mind = useNoteTotal(uid, "mind"),
    plain = useNoteTotal(uid, "plain");
  const pages = { cornell, box, map, mind, plain };
  const positive = (n: number | null | undefined) => (n ? String(n) : undefined);
  const out: Partial<Record<Count, string | undefined>> = {
    ets: positive(ets.data?.length),
    mock: positive(mock.data?.length),
    errors: positive(stats.data?.groups),
    score: positive(latestScore(scores.data)),
    due: stats.data?.due ? `${stats.data.due} ôn` : undefined,
    pomo: focus.data ? `${focus.data.rounds.length}/${dailyRounds}` : undefined,
    hab:
      today.data && today.data.habits.length
        ? `${today.data.habits.filter((h) => h.checked).length}/${today.data.habits.length}`
        : undefined,
    notes: notes.data
      ? notes.data.limit === null
        ? String(notes.data.total)
        : `${notes.data.total}/${notes.data.limit}`
      : undefined,
  };
  for (const [key] of NOTE_METHODS) {
    const n = pages[key].data?.total;
    if (n) out[`nb:${key}`] = `${n} trang`;
  }
  return out;
}
